import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { FollowupFailReason, GeoSource, InteractionChannel, OpportunityStage, PipelineStage, PriorityLevel } from '@mt/shared';
import { api, ApiError } from '@/lib/api';
import type { Customer, FollowUp, Interaction, Paged, QuoteHeader } from '@/lib/types';

/** Khách hàng kèm thông tin danh mục chuẩn P04 (mã tạm TN-, nguồn Asia, field khóa, version). */
export interface CustomerMaster extends Customer {
  status: 'prospect' | 'official';
  dataSource: 'local' | 'asia';
  /** Mã tạm TN- cũ, còn giữ sau khi gắn mã Asia */
  prospectCode: string | null;
  /** Cột DB do Asia sở hữu (code, name, tax_code, address): khóa ô nhập */
  lockedFields: string[];
  version: number;
  paymentTermId: number | null;
  /** Nhóm giá (P06 B2, DEC-096): null = bảng giá chung */
  priceListId: number | null;
  priceListName: string | null;
}

export const CUSTOMER_STATUS_LABELS: Record<CustomerMaster['status'], string> = { prospect: 'Tiềm năng', official: 'Chính thức' };

export type DuplicateSignal = 'tax_code' | 'phone' | 'email' | 'name_province' | 'name_fallback';
export const DUPLICATE_SIGNAL_LABELS: Record<DuplicateSignal, string> = {
  tax_code: 'MST',
  phone: 'SĐT',
  email: 'Email',
  name_province: 'Tên giống + cùng tỉnh',
  name_fallback: 'Tên giống (không rõ tỉnh)',
};
export interface DuplicateCandidate {
  /** Không có với khách ngoài phạm vi xem */
  id?: number;
  code: string;
  name: string;
  status: string;
  ownerName: string | null;
  signals: DuplicateSignal[];
  nameSimilarity: number | null;
  inScope: boolean;
  taxCode?: string | null;
  phone?: string | null;
  email?: string | null;
  address?: string | null;
}

/** Lấy danh sách nghi trùng từ lỗi 409 DUPLICATE (trùng MST exact thì không có candidates → trả null). */
export function duplicateCandidates(e: unknown): DuplicateCandidate[] | null {
  if (!(e instanceof ApiError) || e.status !== 409 || e.code !== 'DUPLICATE') return null;
  const c = (e.details as { candidates?: unknown } | undefined)?.candidates;
  return Array.isArray(c) && c.length ? (c as DuplicateCandidate[]) : null;
}

/** Map lỗi validate (details là mảng {path, message}) thành {field: message}. */
export function fieldErrors(e: unknown): Record<string, string> | null {
  if (!(e instanceof ApiError) || !Array.isArray(e.details)) return null;
  return Object.fromEntries((e.details as { path: string; message: string }[]).map((d) => [d.path, d.message]));
}

export const newIdempotencyKey = () =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;

export interface CustomerFilter {
  q?: string;
  stage?: PipelineStage;
  status?: CustomerMaster['status'];
  ownerUserId?: number;
  sort?: string;
  dir?: 'asc' | 'desc';
  page: number;
  pageSize: number;
}

export const useCustomerList = (f: CustomerFilter) =>
  useQuery({ queryKey: ['customers', 'list', f], queryFn: () => api.get<Paged<CustomerMaster>>('/customers', { ...f }), placeholderData: keepPreviousData });

export const useCustomer = (id: number | null) =>
  useQuery({ queryKey: ['customers', 'detail', id], queryFn: () => api.get<CustomerMaster>(`/customers/${id}`), enabled: !!id });

export const useInteractions = (id: number | null) =>
  useQuery({ queryKey: ['customers', 'interactions', id], queryFn: () => api.get<Interaction[]>(`/customers/${id}/interactions`), enabled: !!id });

export const useCustomerQuotes = (id: number | null) =>
  useQuery({
    queryKey: ['quotes', 'list', { customerId: id }],
    queryFn: () => api.get<Paged<QuoteHeader>>('/quotes', { customerId: id, pageSize: 50, sort: 'quote_date', dir: 'desc' }),
    enabled: !!id,
  });

export type FollowUpWhen = 'today' | 'overdue' | 'upcoming' | 'done' | 'all';
export const useFollowUps = (p: { when: FollowUpWhen; scope?: 'mine' | 'all'; customerId?: number; leadId?: number; opportunityId?: number; assignedTo?: number }, enabled = true) =>
  useQuery({ queryKey: ['follow-ups', p], queryFn: () => api.get<FollowUp[]>('/follow-ups', { ...p }), enabled });

// ---------------------------------------------------------------------------
// DEC-152: Việc cần làm – Sales Follow-up Control Center
// ---------------------------------------------------------------------------
export type BoardView = 'all' | 'overdue' | 'today' | 'upcoming' | 'high_profit' | 'high_value' | 'high_win' | 'vip' | 'has_dm' | 'no_contact' | 'failed';
export type BoardSort = 'priority' | 'profit' | 'value' | 'win' | 'overdue' | 'due';
export interface BoardItem {
  id: number;
  title: string;
  status: 'pending' | 'done' | 'skipped';
  result: string | null;
  doneAt: string | null;
  assignedTo: number;
  assignedName: string;
  customer: { id: number | null; code: string | null; name: string; type: string | null; isVip: boolean; leadId: number | null; leadCode: string | null };
  opportunity: {
    id: number;
    code: string;
    name: string;
    stage: OpportunityStage;
    version: number;
    ownerName: string;
    failedAt: string | null;
    failReason: FollowupFailReason | null;
    failNote: string | null;
  } | null;
  quote: { id: number; code: string } | null;
  value: number | null;
  /** undefined = không có quyền xem lãi; null = chưa đủ giá vốn */
  profit?: number | null;
  margin?: number | null;
  win: number | null;
  winIsDefault: boolean;
  decisionMaker: { name: string; position: string | null; phone: string | null } | null;
  /** Người nhận liên hệ: người quyết định, nếu không thì liên hệ chính (zalo đã rơi về SĐT khi trống) */
  contact: { name: string | null; phone: string | null; zalo: string | null; email: string | null };
  lastContactAt: string | null;
  lastContactChannel: InteractionChannel | null;
  daysSinceContact: number | null;
  followup: {
    day: number | null;
    dueDate: string;
    daysUntilDue: number;
    isOverdue: boolean;
    isToday: boolean;
    next: { day: number; dueDate: string } | null;
    nextIsFail: boolean;
  };
  businessScore: number;
  actionScore: number;
  priorityLevel: PriorityLevel;
  reasons: string[];
  recommendation: string | null;
}
export interface BoardResponse {
  items: BoardItem[];
  total: number;
  page: number;
  pageSize: number;
  canSeeProfit: boolean;
  truncated: boolean;
  summary: { overdue: number; today: number; upcoming: number; totalValue: number; totalProfit?: number; avgWin: number | null; failed: number };
}
export const useFollowUpBoard = (p: { scope: 'mine' | 'all'; assignedTo?: number; view: BoardView; sort: BoardSort; q?: string; page: number; pageSize: number }) =>
  useQuery({ queryKey: ['follow-ups', 'board', p], queryFn: () => api.get<BoardResponse>('/follow-ups/board', { ...p }), placeholderData: keepPreviousData });

export function useReactivateFollowups() {
  const inv = useInvalidateCrm();
  return useMutation({ mutationFn: (opportunityId: number) => api.post(`/opportunities/${opportunityId}/follow-ups/reactivate`, {}), onSuccess: inv });
}

export const useFollowUpCounts = (enabled = true) =>
  useQuery({
    queryKey: ['follow-ups', 'counts'],
    enabled,
    queryFn: () => api.get<{ overdue: number; today: number; next7: number }>('/follow-ups/counts'),
    refetchInterval: 5 * 60_000,
  });

export interface PipelineCard {
  id: number;
  code: string;
  name: string;
  stage: PipelineStage;
  stageChangedAt: string;
  lastInteractionAt: string | null;
  ownerName: string | null;
  openQuoteTotal: number;
  nextFollowUp: string | null;
  stageCount: number;
}
export const usePipeline = (p: { ownerUserId?: number; q?: string }) =>
  useQuery({ queryKey: ['customers', 'pipeline', p], queryFn: () => api.get<PipelineCard[]>('/crm/pipeline', { ...p }), placeholderData: keepPreviousData });

/** Sau mọi thao tác CRM: làm mới khách hàng, follow-up. */
function useInvalidateCrm() {
  const qc = useQueryClient();
  return () => {
    qc.invalidateQueries({ queryKey: ['customers'] });
    qc.invalidateQueries({ queryKey: ['follow-ups'] });
    // P05: follow-up/tương tác còn hiện ở lead, cơ hội (thẻ Kanban có follow-up kế)
    qc.invalidateQueries({ queryKey: ['opportunities'] });
    qc.invalidateQueries({ queryKey: ['leads'] });
  };
}

export function useChangeStage() {
  const inv = useInvalidateCrm();
  return useMutation({
    mutationFn: ({ id, stage }: { id: number; stage: PipelineStage }) => api.post(`/customers/${id}/stage`, { stage }),
    onSuccess: inv,
  });
}

export function useAddInteraction() {
  const inv = useInvalidateCrm();
  return useMutation({
    mutationFn: ({ customerId, ...body }: { customerId: number; channel: InteractionChannel; direction?: 'in' | 'out' | null; content: string; contactId?: number | null }) =>
      api.post(`/customers/${customerId}/interactions`, body),
    onSuccess: inv,
  });
}

export function useCreateFollowUp() {
  const inv = useInvalidateCrm();
  return useMutation({
    mutationFn: (body: { customerId?: number | null; leadId?: number | null; opportunityId?: number | null; title: string; dueDate: string; quoteId?: number | null }) =>
      api.post('/follow-ups', body),
    onSuccess: inv,
  });
}

export function useCompleteFollowUp() {
  const inv = useInvalidateCrm();
  return useMutation({
    mutationFn: ({
      id,
      ...body
    }: {
      id: number;
      result: string;
      channel: InteractionChannel;
      nextDueDate?: string | null;
      nextTitle?: string | null;
      /** DEC-152: bước tiếp của chuỗi F1→F30 */
      next?: 'auto' | 'none' | 'fail';
      failReason?: FollowupFailReason | null;
      failNote?: string | null;
    }) => api.post<{ ok: true; next: { day: number | null; dueDate: string } | null; failed: boolean }>(`/follow-ups/${id}/complete`, body),
    onSuccess: inv,
  });
}

export function useSkipFollowUp() {
  const inv = useInvalidateCrm();
  return useMutation({ mutationFn: ({ id, reason }: { id: number; reason: string }) => api.post(`/follow-ups/${id}/skip`, { reason }), onSuccess: inv });
}

export function useSaveCustomer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: number | null; body: unknown }) => (id ? api.put<CustomerMaster>(`/customers/${id}`, body) : api.post<CustomerMaster>('/customers', body)),
    onSuccess: (c) => {
      qc.setQueryData(['customers', 'detail', c.id], c);
      qc.invalidateQueries({ queryKey: ['customers', 'list'] });
      qc.invalidateQueries({ queryKey: ['customers', 'pipeline'] });
    },
  });
}

export const checkDuplicates = (p: { taxCode?: string; phone?: string; email?: string; name?: string; address?: string; excludeId?: number }) =>
  api.get<{ candidates: DuplicateCandidate[] }>('/customers/duplicate-check', p).then((r) => r.candidates);

export interface PaymentTerm {
  id: number;
  code: string;
  name: string;
  dueDays?: number;
  isActive: boolean;
}
export const usePaymentTerms = () =>
  useQuery({ queryKey: ['payment-terms', 'active'], queryFn: () => api.get<PaymentTerm[]>('/payment-terms', { active: true }), staleTime: 5 * 60_000 });

// ---- Địa chỉ giao hàng / chi nhánh ----
export interface CustomerAddress {
  id: number;
  customerId: number;
  label: string;
  address: string;
  receiverName: string | null;
  receiverPhone: string | null;
  isDefault: boolean;
  isActive: boolean;
  note: string | null;
  asiaCode?: string | null;
  lastUsedAt?: string | null;
  /** P11 B5: tọa độ điểm giao */
  lat?: number | null;
  lng?: number | null;
  geoSource?: GeoSource | null;
}
export interface AddressInput {
  label: string;
  address: string;
  receiverName: string;
  receiverPhone: string;
  isDefault: boolean;
  isActive: boolean;
  note: string;
  lat: number | null;
  lng: number | null;
  geoSource: GeoSource | null;
}
export const useAddresses = (customerId: number) =>
  useQuery({ queryKey: ['customers', 'addresses', customerId], queryFn: () => api.get<CustomerAddress[]>(`/customers/${customerId}/addresses`) });

export function useSaveAddress(customerId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: number | null; body: AddressInput }) =>
      id ? api.put<CustomerAddress>(`/customers/${customerId}/addresses/${id}`, body) : api.post<CustomerAddress>(`/customers/${customerId}/addresses`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['customers', 'addresses', customerId] }),
  });
}
export function useDeleteAddress(customerId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.del(`/customers/${customerId}/addresses/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['customers', 'addresses', customerId] }),
  });
}

// ---- Gắn mã Asia ----
export interface LinkSuggestion {
  id: number;
  customerId: number;
  customerCode: string | null;
  customerName: string | null;
  taxCode: string | null;
  customerStatus: CustomerMaster['status'] | null;
  asiaCode: string;
  asia: { code: string; name: string; taxCode: string | null; address: string | null } | null;
  detectedAt: string;
  status: 'open' | 'resolved' | 'ignored';
}
export const useLinkSuggestions = (enabled = true) =>
  useQuery({
    queryKey: ['customers', 'link-suggestions'],
    queryFn: () => api.get<{ items: LinkSuggestion[] }>('/customers/link-suggestions').then((r) => r.items),
    enabled,
  });

export function useLinkAsia() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ customerId, suggestionId, key }: { customerId: number; suggestionId: number; key: string }) =>
      api.post<CustomerMaster>(`/customers/${customerId}/link-asia`, { suggestionId }, { 'Idempotency-Key': key }),
    onSuccess: (c) => {
      qc.setQueryData(['customers', 'detail', c.id], c);
      qc.invalidateQueries({ queryKey: ['customers'] });
    },
  });
}

// ---- Gộp khách tiềm năng ----
export interface MergeSide {
  id: number;
  code: string;
  name: string;
  status: CustomerMaster['status'];
}
export interface MergePreview {
  source: MergeSide;
  target: MergeSide;
  counts: Record<string, number>;
  items: { table: string; label: string; count: number }[];
}
export const mergePreview = (sourceId: number, targetId: number) => api.post<MergePreview>(`/customers/${sourceId}/merge/preview`, { targetId });

export function useMergeCustomer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ sourceId, targetId, reason, key }: { sourceId: number; targetId: number; reason: string; key: string }) =>
      api.post<{ sourceId: number; targetId: number; moved: Record<string, number> }>(`/customers/${sourceId}/merge`, { targetId, reason }, { 'Idempotency-Key': key }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['customers'] });
      qc.invalidateQueries({ queryKey: ['follow-ups'] });
      qc.invalidateQueries({ queryKey: ['quotes'] });
    },
  });
}

/** YYYY-MM-DD theo giờ máy người dùng (VN). */
export const localToday = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
export const plusDays = (iso: string, n: number) => {
  const d = new Date(`${iso}T00:00:00`);
  d.setDate(d.getDate() + n);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

// CR-012 PR 2: email follow-up theo mốc F (mẫu sửa được), AI viết lại, gửi qua SMTP công ty
export interface EmailDraft {
  followUpId: number;
  to: string;
  contactName: string | null;
  customerName: string;
  sequenceDay: number | null;
  templateCode: string | null;
  templateName: string | null;
  subject: string;
  body: string;
  quote: { id: number; code: string } | null;
  attachQuote: boolean;
  sentBefore: number;
  smtpReady: boolean;
  from: string | null;
  replyTo: string | null;
}
export interface EmailTemplate {
  id: number;
  code: string;
  fromDay: number | null;
  name: string;
  subject: string;
  body: string;
  attachQuote: boolean;
  isActive: boolean;
  version: number;
  updatedAt: string;
  updatedByName: string | null;
}
export const useEmailDraft = (followUpId: number | null) =>
  useQuery({
    queryKey: ['follow-ups', followUpId, 'email-draft'],
    enabled: followUpId !== null,
    queryFn: () => api.get<EmailDraft>(`/follow-ups/${followUpId}/email-draft`),
    staleTime: 0,
    gcTime: 0,
  });
export const useAiRewriteEmail = () =>
  useMutation({
    mutationFn: ({ id, ...body }: { id: number; subject: string; body: string }) =>
      api.post<{ subject: string; body: string; model: string }>(`/follow-ups/${id}/email-draft/ai`, body),
  });
export function useSendFollowUpEmail() {
  const inv = useInvalidateCrm();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: number; to: string; cc?: string | null; subject: string; body: string; attachQuote: boolean; templateCode?: string | null; aiUsed: boolean; complete: boolean }) =>
      api.post<{ ok: true; emailId: number; completed: boolean; next: { day: number | null; dueDate: string } | null; failed: boolean }>(`/follow-ups/${id}/email`, body),
    onSuccess: inv,
  });
}
export const useEmailTemplates = (enabled = true) =>
  useQuery({ queryKey: ['email-templates'], enabled, queryFn: () => api.get<{ items: EmailTemplate[]; smtpReady: boolean }>('/email-templates') });
export function useUpdateEmailTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: number; name: string; subject: string; body: string; attachQuote: boolean; isActive: boolean; version: number }) =>
      api.put<EmailTemplate>(`/email-templates/${id}`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['email-templates'] }),
  });
}
